import {
  useCompanyData,
  useCompanyMembers,
  useGetWorkRequestsByCompany,
} from '@commenda/api';
import {
  OnboardingSteps,
  TCompanyData,
} from '@commenda/types/src/company/types';
import { CompanyCreationSource } from '@prisma/client';
import { isGeneralOnboardingStepCompleted } from 'components/Setup/OnboardingSteps/utils';
import { useConfig } from 'context/useConfig';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

const ONBOARDING_FLOW_RELEASE_DATE = new Date('2025-03-13T17:46:46.204Z');

const shouldShowNewOnboardingFlow = (company: TCompanyData) => {
  return new Date(company.createdAt) > ONBOARDING_FLOW_RELEASE_DATE;
};

/**
 * Checks if the user needs to complete onboarding and redirects if so.
 * Returns `isLoading: true` while checking or redirecting.
 */
export function useOnboardingRedirect() {
  const { companyId, user } = useConfig();
  const router = useRouter();
  const [isLoading, setLoading] = useState(true);

  const { data: company } = useCompanyData(companyId);
  const { data: companyMembers } = useCompanyMembers(companyId);
  const { data: workRequests } = useGetWorkRequestsByCompany(companyId);

  useEffect(() => {
    if (!workRequests || !company || !companyMembers) return;

    // Has existing work requests — no onboarding needed
    if (workRequests.workRequests.length) {
      setLoading(false);
      return;
    }

    // Company created before the onboarding flow — skip
    if (!shouldShowNewOnboardingFlow(company)) {
      setLoading(false);
      return;
    }

    // Created by affiliate firm — skip onboarding
    if (company.createdBy === CompanyCreationSource.AFFILIATE_FIRM) {
      setLoading(false);
      return;
    }

    const isGeneralOnboardingCompleted = isGeneralOnboardingStepCompleted(
      user?.id,
      company,
      companyMembers.members
    );

    if (!isGeneralOnboardingCompleted) {
      router.push('/setup/onboarding').then(() => setLoading(false));
      return;
    }

    const isAddEntitiesStepCompleted =
      company?.onboardingInformation?.onboardingStepsInformation?.completedSteps?.includes(
        OnboardingSteps.ADD_ENTITIES
      );

    if (!isAddEntitiesStepCompleted) {
      router.push('/setup/dashboard').then(() => setLoading(false));
      return;
    }

    setLoading(false);
  }, [workRequests, router, company, companyMembers, user?.id]);

  return { isLoading };
}
